import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { History, Shield, Sparkles, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

export const StorySection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <section id="story" className="min-h-screen py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <Badge variant="outline" className="gold-border text-primary mb-4">
              Our Journey
            </Badge>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gradient-gold mb-4">
              Our Story
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-base">
              Building a sustainable legacy in youth football development
            </p>
          </motion.div>

          {/* Timeline Cards */}
          <div className="grid gap-8">
            {/* The Beginning - Since 2015 */}
            <motion.div variants={itemVariants}>
              <Card className="bg-gradient-card gold-border overflow-hidden">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <History className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <Badge className="bg-primary/20 text-primary border-0 mb-2">Since 2015</Badge>
                      <CardTitle className="font-display text-xl text-card-foreground">
                        The Academy&apos;s Journey
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="text-muted-foreground leading-relaxed">
                    Since 2015, the Singapore Cricket Club has run a football academy to develop young players within the Club&apos;s tradition of 
                    <span className="text-primary font-medium"> sporting excellence</span>. Initially delivered by the Club, the Academy later partnered with various external providers. While each brought strengths, their differing brands, values, and football cultures did not always align with SCC&apos;s identity.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* The Decision & Investment - Combined */}
            <motion.div variants={itemVariants}>
              <Card className="bg-gradient-card gold-border overflow-hidden">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <Shield className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <Badge className="bg-primary/20 text-primary border-0 mb-2">The Transition</Badge>
                      <CardTitle className="font-display text-xl text-card-foreground">
                        Bringing the Academy In-House
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="text-muted-foreground leading-relaxed">
                    To protect what makes SCC unique, the Club made the decision to 
                    <span className="text-primary font-semibold"> bring the Academy in-house</span>. 
                    The Club then invested heavily to nurture the programme, including subsidising its operations to keep it accessible and stable as it grew.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* The New Chapter */}
            <motion.div variants={itemVariants}>
              <Card className="bg-gradient-card gold-border overflow-hidden border-success/30">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg bg-success/10">
                      <Sparkles className="h-6 w-6 text-success" />
                    </div>
                    <div>
                      <Badge className="bg-success/20 text-success border-0 mb-2">A New Chapter</Badge>
                      <CardTitle className="font-display text-xl text-card-foreground">
                        Football Section Takes the Lead
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    In 2026, we began the next chapter. SCC entrusted the Football Section to lead the Academy, strengthen its standards, and build a 
                    <span className="text-success font-semibold"> thriving, sustainable platform </span> 
                    for player development. Football is played <span className="text-primary font-semibold">7 days a week</span> at SCC.
                  </p>
                  <div className="glass-card rounded-lg p-4 border border-success/20">
                    <p className="text-success text-sm font-medium mb-2">Our Vision:</p>
                    <p className="text-muted-foreground text-sm">
                      The Academy provides a pathway for young footballers to develop their skills and strive to play at a <span className="text-primary font-semibold">highly competitive level</span>. SCC&apos;s senior team competes in the Singapore Football League (SFL), and our <span className="text-primary font-semibold">SFL Division 1 Champions for 2025</span> have finished in the top 3 for the last 3 consecutive years. This is proof that excellence is achievable within our Club.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* The Football Section */}
            <motion.div variants={itemVariants}>
              <Card className="bg-gradient-card gold-border overflow-hidden">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <Badge className="bg-primary/20 text-primary border-0 mb-2">The Football Section</Badge>
                      <CardTitle className="font-display text-xl text-card-foreground">
                        A Thriving Football Community
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    The SCC Football Section boasts <span className="text-primary font-semibold">8 teams</span>, comprising 4 social teams, 3 competitive teams, and our ladies team bridging both worlds. Football is played 
                    <span className="text-primary font-medium"> 7 days a week</span> across all our teams and the Academy.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Everyone who plays for SCC is required to be a signed-up member of the Club, creating a close-knit community united by their love of the game. The Academy is the one exception. Young players and their families are welcomed to train with us and experience the SCC culture without needing to be Club members.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default StorySection;
